import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { networkService } from '../services/networkService';
import NetworkFlowchart from './NetworkFlowchart';

export const NetworkTree = () => {
  const [networkData, setNetworkData] = useState([]);
  const [networkStats, setNetworkStats] = useState({
    directInvitees: 0,
    indirectInvitees: 0,
    treeMerges: 0,
    totalConnections: 0,
    networkDepth: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentUser } = useAuth();
  const [inviteCode, setInviteCode] = useState(null);
  const [inviteCodeLoading, setInviteCodeLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [mergeCode, setMergeCode] = useState('');
  const [mergeLoading, setMergeLoading] = useState(false);
  const [mergeStatus, setMergeStatus] = useState({ type: null, message: '' });

  const loadNetworkData = useCallback(async () => {
    if (!currentUser) {
      setError('You must be logged in to view your network');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const data = await networkService.getNetworkCluster();
      console.log('Network data received:', data);
      
      // Extract the roots array from the response
      const roots = Array.isArray(data?.roots) ? data.roots : [];
      console.log('Network roots:', roots);
      setNetworkData(roots);

      // Calculate network statistics
      const calculateStats = (roots, treeMergesFromBackend) => {
        let directInvitees = 0;
        let indirectInvitees = 0;
        let totalConnections = 0;
        let maxLevel = 0;

        const countChildren = (node, level) => {
          if (level > maxLevel) maxLevel = level;
          const children = node.children || [];

          for (const child of children) {
            totalConnections++;
            if (level === 0) {
              // Direct child
              directInvitees++;
              countChildren(child, level + 1);
            } else {
              // Indirect child
              indirectInvitees++;
              countChildren(child, level + 1);
            }
          }
        };

        for (const root of roots) {
          countChildren(root, 0);
        }

        return {
          directInvitees,
          indirectInvitees,
          treeMerges: treeMergesFromBackend || 0,
          totalConnections,
          networkDepth: maxLevel + 1
        };
      };

      const stats = calculateStats(roots, data?.treeMergesCount);
      setNetworkStats(stats);
      setError(null);
    } catch (err) {
      setError('Failed to load network data');
      console.error('Network data error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    loadNetworkData();
    fetchInviteCode();
  }, [currentUser, loadNetworkData]);

  // Load Monetag interstitial ad script
  useEffect(() => {
    const script = document.createElement('script');
    script.dataset.zone = '10110392';
    script.src = 'https://groleegni.net/vignette.min.js';
    script.async = true;

    const targetElement = [document.documentElement, document.body].filter(Boolean).pop();
    if (targetElement) {
      targetElement.appendChild(script);
    }

    // Cleanup function to remove the script when component unmounts
    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  const fetchInviteCode = async () => {
    try {
      setInviteCodeLoading(true);
      
      // Get token from localStorage (same as Profile.js)
      const token = localStorage.getItem('sbwom_token') || sessionStorage.getItem('sbwom_token');
      
      if (token) {
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        const response = await fetch(`${API_BASE_URL}/network/invite`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        
        const data = await response.json();

        if (data && data.inviteCode) {
          setInviteCode(data.inviteCode);
        }
      }
    } catch (err) {
      console.error('Failed to fetch invite code:', err);
    } finally {
      setInviteCodeLoading(false);
    }
  };

  const copyToClipboard = () => {
    console.log('=== NETWORKTREE COPY TO CLIPBOARD DEBUG ===');
    console.log('inviteCode:', inviteCode);
    console.log('copied state:', copied);

    if (!inviteCode) {
      console.error('ERROR: No invite code to copy');
      alert('No invite code available to copy. Please try generating an invite code first.');
      return;
    }

    console.log('Attempting to copy to clipboard:', inviteCode);
    navigator.clipboard.writeText(inviteCode)
      .then(() => {
        console.log('SUCCESS: Invite code copied to clipboard:', inviteCode);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(err => {
        console.error('ERROR: Failed to copy to clipboard:', err);
        console.error('Clipboard error details:', {
          name: err.name,
          message: err.message,
          stack: err.stack
        });
        setCopied(false);
        alert('Failed to copy invite code. Please try again or copy manually.');
      });
  };

  const handleMergeSubmit = async (event) => {
    event.preventDefault();
    const trimmedCode = mergeCode.trim();

    if (!trimmedCode) {
      setMergeStatus({ type: 'error', message: 'Enter an invite code to merge.' });
      return;
    }

    try {
      setMergeLoading(true);
      setMergeStatus({ type: null, message: '' });
      const response = await networkService.mergeNetworkByCode(trimmedCode);
      setMergeStatus({
        type: response.alreadyMerged ? 'info' : 'success',
        message: response.message || (response.alreadyMerged ? 'Networks are already connected.' : 'Networks merged successfully.'),
      });
      setMergeCode('');
      await loadNetworkData();
    } catch (err) {
      setMergeStatus({
        type: 'error',
        message: err?.message || 'Failed to merge networks. Please try again.',
      });
    } finally {
      setMergeLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="text-center">
          <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full text-brown-600" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p className="mt-2 text-brown-600">Loading network data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="network-layout main-container" style={{ backgroundColor: 'transparent' }}>
      {/* Left Sidebar with Network Statistics */}
      <div className="network-sidebar">
        <h2 className="text-lg font-bold text-brown-800 mb-3">Your Network</h2>
        
        {/* Mobile Side-by-Side Container */}
        <div className="network-sidebar-content">
          {/* Network Statistics Panel */}
          <div className="network-stats-panel bg-white rounded-xl shadow-lg flex-1 md:flex-initial md:w-64">
            <h3 className="text-sm font-semibold text-brown-800 mb-2">Network Statistics</h3>
            
            <div className="network-stats-list">
              {/* Direct Invitees */}
                <div className="network-stat-card bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center">
                    <svg className="w-3 h-3 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                    </svg>
                    <div className="network-stat-card__content">
                      <div className="network-stat-card__value text-sm font-bold text-green-800">{networkStats.directInvitees}</div>
                      <div className="network-stat-card__label text-xs text-green-700">Direct Invitees</div>
                    </div>
                  </div>
                </div>
              
              {/* Indirect Invitees */}
                <div className="network-stat-card bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center">
                    <svg className="w-3 h-3 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                    </svg>
                    <div className="network-stat-card__content">
                      <div className="network-stat-card__value text-sm font-bold text-blue-800">{networkStats.indirectInvitees}</div>
                      <div className="network-stat-card__label text-xs text-blue-700">Indirect Invitees</div>
                    </div>
                  </div>
                </div>
              
              {/* Tree Merges */}
                <div className="network-stat-card bg-purple-50 rounded-lg border border-purple-200">
                  <div className="flex items-center">
                    <svg className="w-3 h-3 text-purple-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                    <div className="network-stat-card__content">
                      <div className="network-stat-card__value text-sm font-bold text-purple-800">{networkStats.treeMerges}</div>
                      <div className="network-stat-card__label text-xs text-purple-700">Tree Merges</div>
                    </div>
                  </div>
                </div>
              
              {/* Total Connections */}
                <div className="network-stat-card bg-brown-50 rounded-lg border border-brown-200">
                  <div className="flex items-center">
                    <svg className="w-3 h-3 text-brown-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                    </svg>
                    <div className="network-stat-card__content">
                      <div className="network-stat-card__value text-sm font-bold text-brown-800">{networkStats.totalConnections}</div>
                      <div className="network-stat-card__label text-xs text-brown-700">Total Connections</div>
                    </div>
                  </div>
                </div>
                
                {/* Network Depth */}
                <div className="network-stat-card bg-yellow-50 rounded-lg border border-yellow-200">
                  <div className="flex items-center">
                    <svg className="w-3 h-3 text-yellow-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                    </svg>
                    <div className="network-stat-card__content">
                      <div className="network-stat-card__value text-sm font-bold text-yellow-800">{networkStats.networkDepth}</div>
                      <div className="network-stat-card__label text-xs text-yellow-700">Network Depth</div>
                    </div>
                  </div>
                </div>
            </div>
          </div>
          
          {/* Invite Code Section - Reduced Size */}
          <div className="network-invite-panel bg-white rounded-xl shadow-lg flex-1 md:flex-initial md:w-64">
            <h3 className="text-sm font-semibold text-brown-800 mb-2">Invite Friends</h3>
            <p className="text-brown-600 text-xs mb-1">
              Grow your network by sharing your invite code!
            </p>
            
            {inviteCodeLoading ? (
              <div className="flex items-center justify-center py-2">
                <div className="spinner-border animate-spin inline-block w-3 h-3 border-2 rounded-full text-green-600" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
                <span className="ml-1 text-brown-600 text-xs">Generating...</span>
              </div>
            ) : inviteCode ? (
              <div>
                <div className="mb-1">
                  <div className="bg-white border border-green-300 rounded px-2 py-1 font-mono text-xs text-green-700">
                    {inviteCode}
                  </div>
                </div>
                
                <button
                  onClick={copyToClipboard}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-1 px-2 rounded text-xs focus:outline-none focus:shadow-outline"
                >
                  {copied ? 'Copied!' : 'Copy Invite Code'}
                </button>

                <div className="mt-2 pt-2 border-t border-green-100">
                  <h4 className="text-xs font-semibold text-brown-700 mb-1">Already on SBWOM?</h4>
                  <p className="text-brown-600 text-[11px] mb-1">
                    Paste a friend's invite code to join networks.
                  </p>
                  <form onSubmit={handleMergeSubmit} className="network-merge-form">
                    <input
                      type="text"
                      value={mergeCode}
                      onChange={event => setMergeCode(event.target.value.toUpperCase())}
                      placeholder="Friend's invite code"
                      className="w-full border border-green-200 rounded px-2 py-1 text-xs focus:outline-none focus:border-green-500"
                      maxLength={16}
                      disabled={mergeLoading}
                    />
                    {mergeStatus.message && (
                      <div
                        className={`text-[11px] ${
                          mergeStatus.type === 'success' ? 'text-green-600' :
                          mergeStatus.type === 'info' ? 'text-brown-600' :
                          'text-red-600'
                        }`}
                      >
                        {mergeStatus.message}
                      </div>
                    )}
                    <button
                      type="submit"
                      className={`w-full bg-green-600 hover:bg-green-700 text-white font-bold py-1 px-2 rounded text-xs focus:outline-none focus:shadow-outline ${
                        mergeLoading ? 'opacity-60 cursor-not-allowed' : ''
                      }`}
                      disabled={mergeLoading}
                    >
                      {mergeLoading ? 'Merging...' : 'Merge Networks'}
                    </button>
                  </form>
                </div>
              </div>
            ) : (
              <div className="text-center py-2">
                  <button
                    onClick={copyToClipboard}
                    className="bg-green-600 hover:bg-green-700 text-white font-bold py-1 px-2 rounded text-xs focus:outline-none focus:shadow-outline"
                  >
                    {copied ? 'Copied!' : 'Copy Invite Code'}
                  </button>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Main Content Area with Network Flowchart */}
      <div className="network-content">
        <div className="bg-white rounded-xl shadow-lg p-4 h-full">
          {networkData && networkData.length > 0 ? (
            <div className="h-full">
              {/* Network visualization */}
              <div className="network-graph bg-brown-50 rounded-lg overflow-hidden">
                <NetworkFlowchart data={networkData} />
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M12 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                  </svg>
                </div>
                <h3 className="text-sm font-semibold text-brown-800 mb-2">No Network Data</h3>
                <p className="text-brown-600 text-xs mb-3">Start building your network by inviting friends and sharing campaigns</p>
                
                {inviteCode && (
                  <div className="bg-green-50 p-3 rounded-lg border border-green-200 max-w-xs mx-auto">
                    <p className="text-green-700 text-xs mb-2">Your invite code:</p>
                    <div className="bg-white border border-green-300 rounded px-2 py-1 font-mono text-xs text-green-700">
                      {inviteCode}
                    </div>
                    <button
                      onClick={copyToClipboard}
                      className="mt-2 w-full bg-green-600 hover:bg-green-700 text-white font-bold py-1 px-2 rounded text-xs focus:outline-none focus:shadow-outline"
                    >
                      {copied ? 'Copied!' : 'Copy Invite Code'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NetworkTree;
